import React, { useState, useCallback } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import StarIcon from "@mui/icons-material/Star";
import { TbCalendarPlus } from "react-icons/tb";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EventNoteIcon from "@mui/icons-material/EventNote";
import CalendarModal from "./CalendarModal";
import { theme } from "../../theme/colors";
import { reportIssue } from "../../utils/issueReportApi";
import { getMeasuresFromElection } from "../../utils/ballotMeasuresApi";

const TYPE_LABELS = {
  presidentialPrimary: "Presidential Primary",
  state: "State",
  stateSenate: "State Senate",
  stateHouse: "State House",
  congressional: "Congressional",
  senate: "Senate",
  county: "County",
  subCounty: "Sub-County",
  municipal: "Municipal",
  subMunicipal: "Sub-Municipal",
  school: "School",
  special: "Special",
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

const getDaysUntil = (dateStr) => {
  if (!dateStr) return null;
  try {
    const electionDate = new Date(dateStr + "T00:00:00");
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.ceil((electionDate - today) / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
};

const buildCalendarNotes = (election) => {
  const lines = [];

  const regOnline = election.registration?.online?.deadline?.date;
  const regByMail = election.registration?.byMail?.deadline?.date;
  const regInPerson = election.registration?.inPerson?.deadline?.date;
  if (regOnline || regByMail || regInPerson) {
    lines.push("");
    lines.push("REGISTRATION DEADLINES:");
    if (regOnline) lines.push(`  Online: ${formatDate(regOnline)}`);
    if (regByMail) lines.push(`  By mail: ${formatDate(regByMail)}`);
    if (regInPerson) lines.push(`  In person: ${formatDate(regInPerson)}`);
  }

  const earlyStart = election.voting?.early?.startDate;
  const earlyEnd = election.voting?.early?.endDate;
  if (earlyStart) {
    lines.push("");
    lines.push(
      earlyEnd
        ? `EARLY VOTING: ${formatDate(earlyStart)} – ${formatDate(earlyEnd)}`
        : `EARLY VOTING STARTS: ${formatDate(earlyStart)}`
    );
  }

  const mailDeadline =
    election.voting?.byMail?.deadline?.ballotRequest?.date;
  const mailReturn =
    election.voting?.byMail?.deadline?.returnByMail?.date;
  if (mailDeadline || mailReturn) {
    lines.push("");
    lines.push("VOTE BY MAIL:");
    if (mailDeadline) lines.push(`  Request ballot by: ${formatDate(mailDeadline)}`);
    if (mailReturn) lines.push(`  Return by mail by: ${formatDate(mailReturn)}`);
  }

  const pollingUrl = election.pollingLocationUrl;
  const canonicalUrl = election.canonicalUrl;
  const website = election.website;
  if (pollingUrl || canonicalUrl || website) {
    lines.push("");
    lines.push("LINKS:");
    if (pollingUrl) lines.push(`  Find your polling place: ${pollingUrl}`);
    if (canonicalUrl) lines.push(`  Election details: ${canonicalUrl}`);
    if (website) lines.push(`  Official site: ${website}`);
  }

  lines.push("");
  lines.push("Powered by Democracy Works | CivixThread");

  return lines.join("\n");
};

// Build the list of dates the user can pick from in the "Add to calendar"
// modal. Families of related deadlines that fall on the same date are merged
// into a single option (e.g. if online / mail / in-person registration all
// close on the same day, we show one "Register by (online, mail, in person)"
// row instead of three duplicates). Mirrors the mobile implementation in
// mobile/src/components/UpcomingElectionsComponents/DemocracyWorksElections.js.
const buildDateOptions = (election) => {
  const name = election.description || "Election";
  const options = [];

  if (election.date) {
    options.push({
      id: "election_day",
      label: "Election Day",
      date: election.date,
      calendarTitle: name,
      notesPrefix: null,
    });
  }

  const regOnline = election.registration?.online?.deadline?.date;
  if (regOnline) {
    options.push({
      id: "register_online",
      label: "Register by (online)",
      date: regOnline,
      calendarTitle: `Register to vote: ${name}`,
      notesPrefix: "Online registration deadline",
    });
  }

  const regByMail = election.registration?.byMail?.deadline?.date;
  if (regByMail) {
    options.push({
      id: "register_mail",
      label: "Register by (mail)",
      date: regByMail,
      calendarTitle: `Register to vote: ${name}`,
      notesPrefix: "Mail registration deadline",
    });
  }

  const regInPerson = election.registration?.inPerson?.deadline?.date;
  if (regInPerson) {
    options.push({
      id: "register_in_person",
      label: "Register by (in person)",
      date: regInPerson,
      calendarTitle: `Register to vote: ${name}`,
      notesPrefix: "In-person registration deadline",
    });
  }

  const earlyStart = election.voting?.early?.startDate;
  if (earlyStart) {
    options.push({
      id: "early_voting_start",
      label: "Early voting starts",
      date: earlyStart,
      calendarTitle: `Early voting: ${name}`,
      notesPrefix: "Early voting begins",
    });
  }

  const mailRequest = election.voting?.byMail?.deadline?.ballotRequest?.date;
  if (mailRequest) {
    options.push({
      id: "mail_ballot_request",
      label: "Request mail ballot by",
      date: mailRequest,
      calendarTitle: `Request mail ballot: ${name}`,
      notesPrefix: "Mail ballot request deadline",
    });
  }

  const mailReturn = election.voting?.byMail?.deadline?.returnByMail?.date;
  if (mailReturn) {
    options.push({
      id: "mail_ballot_return",
      label: "Return mail ballot by",
      date: mailReturn,
      calendarTitle: `Return mail ballot: ${name}`,
      notesPrefix: "Mail ballot return deadline",
    });
  }

  // Group by (family, date) so we can collapse duplicates within a family but
  // NEVER merge across families (registration vs. mail-ballot stay separate
  // even if they happen to fall on the same day).
  const FAMILIES = {
    register_online: "registration",
    register_mail: "registration",
    register_in_person: "registration",
    mail_ballot_request: "mail_ballot",
    mail_ballot_return: "mail_ballot",
  };

  const REG_METHOD_LABELS = {
    register_online: "online",
    register_mail: "mail",
    register_in_person: "in person",
  };

  const grouped = new Map();
  const standalone = [];

  for (const opt of options) {
    const family = FAMILIES[opt.id];
    if (!family) {
      standalone.push(opt);
      continue;
    }
    const key = `${family}::${opt.date}`;
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(opt);
  }

  const merged = [...standalone];
  for (const members of grouped.values()) {
    if (members.length === 1) {
      merged.push(members[0]);
      continue;
    }

    const family = FAMILIES[members[0].id];
    if (family === "registration") {
      const methods = members
        .map((m) => REG_METHOD_LABELS[m.id])
        .filter(Boolean);
      merged.push({
        id: members[0].id,
        label: `Register by (${methods.join(", ")})`,
        date: members[0].date,
        calendarTitle: members[0].calendarTitle,
        notesPrefix: `Registration deadline (${methods.join(", ")})`,
      });
    } else {
      merged.push({
        id: members[0].id,
        label: "Mail ballot deadline",
        date: members[0].date,
        calendarTitle: `Mail ballot: ${name}`,
        notesPrefix: "Mail ballot request & return deadline",
      });
    }
  }

  return merged;
};

const transformDWElection = (election) => ({
  election_name: election.description || "Election Day",
  election_date: election.date || "",
  election_notes: buildCalendarNotes(election),
  dateOptions: buildDateOptions(election),
});

const DeadlineRow = ({ label, dateStr }) => {
  if (!dateStr) return null;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
      <EventNoteIcon sx={{ fontSize: 14, color: "#0f172a" }} />
      <Typography sx={{ fontSize: "0.8rem", color: "#0f172a" }}>
        <strong style={{ fontWeight: 600 }}>{label}:</strong> {formatDate(dateStr)}
      </Typography>
    </Box>
  );
};

const sanitizeHtml = (html) => {
  if (!html || typeof html !== "string") return "";
  // Conservative allowlist sanitizer for DW HTML content (which is mostly
  // <p>, <a>, <ul>, <li>, <strong>, <em>, <br>). We strip <script>, <style>,
  // inline event handlers, and javascript: URLs before rendering.
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/ on[a-z]+="[^"]*"/gi, "")
    .replace(/ on[a-z]+='[^']*'/gi, "")
    .replace(/javascript:/gi, "");
};

const BallotItemCard = ({ children }) => (
  <Box
    sx={{
      p: 1.5,
      backgroundColor: "#ffffff",
      borderRadius: 2,
      border: "1px solid #e2e8f0",
    }}
  >
    {children}
  </Box>
);

// ---------------------------------------------------------------------------
// Candidate / contest helpers
// ---------------------------------------------------------------------------

// Normalize a candidate's name across the various shapes DW / Ballotpedia use.
const getCandidateName = (c) => {
  if (!c || typeof c !== "object") return "";
  return (
    c.name ||
    c.fullName ||
    c.displayName ||
    [c.firstName, c.lastName].filter(Boolean).join(" ") ||
    ""
  );
};

// Strict URL validator: only allow absolute http(s) URLs. Rejects
// `javascript:`, `data:`, `mailto:`, relative paths, and malformed strings so
// we never hand a hostile or surprising link to the user.
const isSafeUrl = (raw) => {
  if (!raw || typeof raw !== "string") return false;
  const trimmed = raw.trim();
  if (!trimmed) return false;
  try {
    const u = new URL(trimmed);
    if (u.protocol !== "http:" && u.protocol !== "https:") return false;
    if (!u.hostname) return false;
    return true;
  } catch {
    return false;
  }
};

// Pull the first usable URL for a candidate (prefer official campaign site,
// then Ballotpedia / canonical profile). Returns `null` unless the value is a
// safe absolute http(s) URL — we never redirect to anything else.
const getCandidateUrl = (c) => {
  if (!c || typeof c !== "object") return null;
  const candidates = [
    c.candidateUrl,
    c.website,
    c.url,
    c.ballotpediaUrl,
    c.canonicalUrl,
  ];
  for (const raw of candidates) {
    if (isSafeUrl(raw)) return raw.trim();
  }
  return null;
};

// Pull a usable party label (full text). Falls back to common abbreviations.
const getCandidateParty = (c) => {
  if (!c || typeof c !== "object") return "";
  const raw =
    c.party ||
    c.partyAffiliation ||
    c.partyLabel ||
    c.partyLabelEn ||
    c.partyName ||
    "";
  return typeof raw === "string" ? raw.trim() : "";
};

const isIncumbent = (c) =>
  Boolean(c && (c.isIncumbent || c.incumbent || c.incumbentStatus === true));

// Map a party string to a strong-contrast badge style. Designed to be
// colorblind-friendly and WCAG-AA-compliant: white text on dark, saturated
// backgrounds. Greens are intentionally olive/forest so they don't collide
// with the CivixThread brand emerald used on the parent card.
const partyBadgeStyle = (party) => {
  const p = (party || "").toLowerCase();
  if (/^(d|dem|democrat)/i.test(p))
    return { bg: "#1d4ed8", fg: "#ffffff", label: "D", full: "Democratic" };
  if (/^(r|rep|republican)/i.test(p))
    return { bg: "#b91c1c", fg: "#ffffff", label: "R", full: "Republican" };
  if (/^(l|lib|libertarian)/i.test(p))
    return { bg: "#a16207", fg: "#ffffff", label: "L", full: "Libertarian" };
  if (/green/i.test(p))
    return { bg: "#166534", fg: "#ffffff", label: "G", full: "Green" };
  if (/peace.*freedom/i.test(p))
    return { bg: "#9d174d", fg: "#ffffff", label: "PF", full: "Peace & Freedom" };
  if (/america.*independent/i.test(p))
    return { bg: "#7c2d12", fg: "#ffffff", label: "AI", full: "American Independent" };
  if (/working.*families/i.test(p))
    return { bg: "#7e22ce", fg: "#ffffff", label: "WF", full: "Working Families" };
  if (/^(i|ind|independent|no.*party|np|nonpartisan|none)/i.test(p))
    return { bg: "#475569", fg: "#ffffff", label: "I", full: "Independent" };
  if (!p) return { bg: "#475569", fg: "#ffffff", label: "?", full: "Party not listed" };
  // Fallback: take first 2 letters, slate background, white text.
  return {
    bg: "#475569",
    fg: "#ffffff",
    label: party.slice(0, 2).toUpperCase(),
    full: party,
  };
};

// Build initials from a candidate name for the avatar circle.
const getInitials = (name) => {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const CandidateRow = ({ candidate }) => {
  const name = getCandidateName(candidate);
  if (!name) return null;
  const party = getCandidateParty(candidate);
  const badge = partyBadgeStyle(party);
  const url = getCandidateUrl(candidate);
  const incumbent = isIncumbent(candidate);

  const Container = url ? "a" : "div";

  return (
    <Box
      component={Container}
      {...(url
        ? {
            href: url,
            target: "_blank",
            rel: "noopener noreferrer",
            "aria-label": `${name}${party ? `, ${badge.full}` : ""}${
              incumbent ? ", incumbent" : ""
            } — open profile in a new tab`,
          }
        : {})}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        py: 1,
        px: 1,
        borderRadius: 1.5,
        textDecoration: "none",
        color: "inherit",
        minHeight: 44,
        transition: "background-color 120ms ease",
        ...(url
          ? {
              cursor: "pointer",
              "&:hover": { backgroundColor: "#f1f5f9" },
              "&:focus-visible": {
                outline: "2px solid #047857",
                outlineOffset: 2,
              },
            }
          : {}),
      }}
    >
      {/* Leading affordance: external-link icon when row links to a profile,
          otherwise initials in a neutral circle. Removes the duplicate
          trailing link icon below for a cleaner row. */}
      <Box
        aria-hidden="true"
        sx={{
          flex: "0 0 auto",
          width: 36,
          height: 36,
          borderRadius: "50%",
          backgroundColor: url ? "#047857" : "#e2e8f0",
          color: url ? "#ffffff" : "#475569",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          fontSize: "0.85rem",
          letterSpacing: "0.02em",
        }}
      >
        {url ? (
          <OpenInNewIcon sx={{ fontSize: 18 }} />
        ) : (
          getInitials(name)
        )}
      </Box>

      {/* Name + meta */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            flexWrap: "wrap",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.95rem",
              fontWeight: 600,
              color: "#0f172a",
              lineHeight: 1.25,
            }}
          >
            {name}
          </Typography>
          {incumbent && (
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.25,
                px: 0.75,
                py: 0.125,
                borderRadius: 1,
                backgroundColor: "#fef3c7",
                color: "#854d0e",
                fontSize: "0.65rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              <StarIcon sx={{ fontSize: 11 }} />
              Incumbent
            </Box>
          )}
        </Box>
        {party && (
          <Typography
            sx={{
              fontSize: "0.78rem",
              color: "#475569",
              lineHeight: 1.4,
              mt: 0.125,
            }}
          >
            {badge.full}
          </Typography>
        )}
      </Box>

    </Box>
  );
};

const ContestMetaLine = ({ contest }) => {
  const bits = [];
  if (contest?.isPrimary || /primary/i.test(contest?.type || "")) {
    bits.push("Primary");
  } else if (contest?.isRunoff || /runoff/i.test(contest?.type || "")) {
    bits.push("Runoff");
  }
  const seats = contest?.numberOfSeats || contest?.voteFor || contest?.seats;
  if (seats && Number.isFinite(Number(seats))) {
    const n = Number(seats);
    bits.push(`Vote for ${n}`);
  }
  if (Array.isArray(contest?.candidates) && contest.candidates.length) {
    bits.push(
      `${contest.candidates.length} candidate${
        contest.candidates.length === 1 ? "" : "s"
      }`
    );
  }
  if (bits.length === 0) return null;
  return (
    <Typography
      sx={{
        fontSize: "0.75rem",
        fontWeight: 600,
        color: "#475569",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        mt: 0.25,
      }}
    >
      {bits.join(" • ")}
    </Typography>
  );
};

const ContestCard = ({ contest }) => {
  const office = contest?.office || contest?.name || "Contest";
  const candidates =
    (Array.isArray(contest?.candidates) && contest.candidates) ||
    (Array.isArray(contest?.runningCandidates) && contest.runningCandidates) ||
    [];
  const INITIAL = 4;
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? candidates : candidates.slice(0, INITIAL);
  const remaining = candidates.length - visible.length;

  const contestUrl = contest?.canonicalUrl || contest?.url || null;

  return (
    <BallotItemCard>
      <Box sx={{ mb: candidates.length > 0 ? 1 : 0 }}>
        <Typography
          component="h3"
          sx={{
            fontSize: "1rem",
            fontWeight: 700,
            color: "#0f172a",
            lineHeight: 1.3,
          }}
        >
          {office}
        </Typography>
        <ContestMetaLine contest={contest} />
      </Box>

      {candidates.length === 0 ? (
        <Typography sx={{ fontSize: "0.85rem", color: "#475569" }}>
          Candidate list not yet published for this contest.
        </Typography>
      ) : (
        <>
          <Stack spacing={0.25} divider={<Box sx={{ height: 1, bgcolor: "#f1f5f9" }} />}>
            {visible.map((c, i) => (
              <CandidateRow key={`cand-${i}-${getCandidateName(c)}`} candidate={c} />
            ))}
          </Stack>
          {remaining > 0 && !showAll && (
            <Button
              onClick={() => setShowAll(true)}
              size="small"
              sx={{
                mt: 1,
                textTransform: "none",
                fontWeight: 600,
                color: theme.primary,
                "&:hover": { backgroundColor: "#ecfdf5" },
              }}
            >
              Show all {candidates.length} candidates
            </Button>
          )}
          {showAll && candidates.length > INITIAL && (
            <Button
              onClick={() => setShowAll(false)}
              size="small"
              sx={{
                mt: 1,
                textTransform: "none",
                fontWeight: 600,
                color: "#475569",
                "&:hover": { backgroundColor: "#f1f5f9" },
              }}
            >
              Show fewer
            </Button>
          )}
        </>
      )}

      {contestUrl && (
        <Box sx={{ mt: 1 }}>
          <a
            href={contestUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: "0.78rem",
              color: theme.primary,
              fontWeight: 600,
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
            aria-label={`More on ${office} (opens in a new tab)`}
          >
            Full contest details
            <OpenInNewIcon sx={{ fontSize: 13 }} />
          </a>
        </Box>
      )}
    </BallotItemCard>
  );
};

// Compact section header used inside the "What's on the ballot" accordion.
// Always renders when its section has data, regardless of whether the other
// section is present — keeps layouts consistent across elections.
const BallotSectionHeading = ({ icon, label, count, suffix }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 0.75,
      mb: 1,
    }}
  >
    {icon}
    <Typography
      component="h4"
      sx={{
        fontSize: "0.78rem",
        fontWeight: 700,
        color: "#334155",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        m: 0,
      }}
    >
      {label}
    </Typography>
    {Number.isFinite(count) && count > 0 && (
      <Typography
        component="span"
        sx={{
          fontSize: "0.72rem",
          fontWeight: 600,
          color: "#475569",
          ml: 0.25,
        }}
      >
        · {count} {suffix}
        {count === 1 ? "" : "s"}
      </Typography>
    )}
  </Box>
);

const BallotDataInner = ({ election }) => {
  const contests =
    election.contests || election.ballotData?.contests || [];
  const measures = getMeasuresFromElection(election);

  const hasContests = Array.isArray(contests) && contests.length > 0;
  const hasMeasures = Array.isArray(measures) && measures.length > 0;

  if (!hasContests && !hasMeasures) {
    return (
      <Typography sx={{ fontSize: "0.85rem", color: "#334155" }}>
        Candidate and ballot-measure details have not been published for this
        election yet. Check back closer to election day.
      </Typography>
    );
  }

  return (
    <Box>
      {hasContests && (
        <Box sx={{ mb: hasMeasures ? 2.5 : 0 }}>
          <Stack spacing={1}>
            {contests.slice(0, 8).map((contest, idx) => (
              <ContestCard
                key={`contest-${idx}-${contest?.office || contest?.name || idx}`}
                contest={contest}
              />
            ))}
          </Stack>
          {contests.length > 8 && (
            <Typography
              sx={{
                mt: 1,
                fontSize: "0.78rem",
                color: "#475569",
                fontStyle: "italic",
              }}
            >
              + {contests.length - 8} more contest
              {contests.length - 8 === 1 ? "" : "s"} on this ballot.
            </Typography>
          )}
        </Box>
      )}

      {hasContests && hasMeasures && (
        <Box
          aria-hidden="true"
          sx={{
            height: 1,
            background:
              "linear-gradient(to right, transparent, #cbd5e1 50%, transparent)",
            mb: 2.5,
          }}
        />
      )}

      {hasMeasures && (
        <Box>
          {hasContests && (
            <BallotSectionHeading
              icon={<EventNoteIcon sx={{ fontSize: 16, color: "#475569" }} />}
              label="Ballot measures"
              count={measures.length}
              suffix="measure"
            />
          )}
          <Stack spacing={1}>
            {measures.slice(0, 6).map((measure, idx) => (
              <BallotItemCard key={`measure-${idx}`}>
                <Typography
                  component="h3"
                  sx={{
                    fontSize: "0.95rem",
                    fontWeight: 700,
                    color: "#0f172a",
                    lineHeight: 1.3,
                    m: 0,
                  }}
                >
                  {measure.title || measure.name || "Ballot measure"}
                </Typography>
                {(measure.summary || measure.description) && (
                  <Box
                    sx={{
                      fontSize: "0.85rem",
                      color: "#334155",
                      mt: 0.75,
                      lineHeight: 1.55,
                      "& p": { m: 0, mb: 0.5 },
                      "& p:last-child": { mb: 0 },
                      "& a": { color: "#065f46", fontWeight: 600 },
                    }}
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(
                        measure.summary || measure.description
                      ),
                    }}
                  />
                )}
              </BallotItemCard>
            ))}
          </Stack>
          {measures.length > 6 && (
            <Typography
              sx={{
                mt: 1,
                fontSize: "0.78rem",
                color: "#475569",
                fontStyle: "italic",
              }}
            >
              + {measures.length - 6} more measure
              {measures.length - 6 === 1 ? "" : "s"} on this ballot.
            </Typography>
          )}
        </Box>
      )}

      <Typography
        sx={{
          mt: 2,
          fontSize: "0.72rem",
          color: "#475569",
          fontStyle: "italic",
        }}
      >
        Ballot data sourced from Ballotpedia via Democracy Works.
      </Typography>
    </Box>
  );
};

const BallotAccordion = ({ election, defaultExpanded = true }) => {
  const [expanded, setExpanded] = useState(Boolean(defaultExpanded));

  const contests =
    election.contests || election.ballotData?.contests || [];
  const measures = getMeasuresFromElection(election);
  const contestCount = Array.isArray(contests) ? contests.length : 0;
  const measureCount = Array.isArray(measures) ? measures.length : 0;
  const summaryBits = [];
  if (contestCount > 0) {
    summaryBits.push(
      `${contestCount} contest${contestCount === 1 ? "" : "s"}`
    );
  }
  if (measureCount > 0) {
    summaryBits.push(
      `${measureCount} measure${measureCount === 1 ? "" : "s"}`
    );
  }
  const summaryText = summaryBits.length ? summaryBits.join(" · ") : null;

  // Stable, unique IDs per election for a11y wiring.
  const baseId = `ballot-${
    election?.ocdId || election?.date || Math.random().toString(36).slice(2)
  }`.replace(/[^a-zA-Z0-9_-]/g, "-");

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      disableGutters
      elevation={0}
      sx={{
        mt: 1.5,
        backgroundColor: "transparent",
        border: "1px solid #e2e8f0",
        borderRadius: 2,
        overflow: "hidden",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: "#0f172a" }} />}
        aria-controls={`${baseId}-content`}
        id={`${baseId}-header`}
        sx={{
          backgroundColor: "#ffffff",
          minHeight: 48,
          "& .MuiAccordionSummary-content": {
            my: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          },
        }}
      >
        <HowToVoteIcon sx={{ fontSize: 18, color: theme.primary }} />
        <Typography
          component="h3"
          sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#0f172a", m: 0 }}
        >
          What's on the ballot
        </Typography>
        {summaryText && (
          <Typography
            component="span"
            sx={{
              fontSize: "0.78rem",
              fontWeight: 600,
              color: "#475569",
              ml: 0.25,
            }}
          >
            · {summaryText}
          </Typography>
        )}
      </AccordionSummary>
      <AccordionDetails
        id={`${baseId}-content`}
        sx={{ backgroundColor: "#ffffff", pt: 0, pb: 2 }}
      >
        <BallotDataInner election={election} />
      </AccordionDetails>
    </Accordion>
  );
};

const SignupNudgeCard = () => (
  <Box
    sx={{
      mt: 1.5,
      p: 2,
      borderRadius: 2,
      background: `linear-gradient(135deg, ${theme.primary} 0%, #065f46 100%)`,
      color: "#ffffff",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
      <RocketLaunchIcon sx={{ fontSize: 20, color: "#ffffff" }} />
      <Typography
        sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#ffffff" }}
      >
        Make impact beyond election day
      </Typography>
    </Box>
    <Typography
      sx={{
        fontSize: "0.85rem",
        color: "rgba(255,255,255,0.95)",
        lineHeight: 1.5,
        mb: 1.5,
      }}
    >
      Create a free CivixThread account to save your ballot, get deadline
      reminders, follow the bills your reps are voting on, and stay engaged
      year-round — not just on election day.
    </Typography>
    <Button
      component={RouterLink}
      to="/auth/signup"
      variant="contained"
      sx={{
        textTransform: "none",
        fontWeight: 700,
        borderRadius: 2,
        background: "#ffffff",
        backgroundColor: "#ffffff",
        color: "#065f46",
        boxShadow: "none",
        minHeight: 44,
        px: 2.5,
        "&.MuiButton-containedPrimary": {
          background: "#ffffff",
          backgroundColor: "#ffffff",
          color: "#065f46",
        },
        "&:hover": {
          background: "#f1f5f9",
          backgroundColor: "#f1f5f9",
          boxShadow: "none",
          color: "#065f46",
          opacity: 1,
        },
      }}
    >
      Create your free account
    </Button>
  </Box>
);

const DemocracyWorksElections = ({
  elections = [],
  hideReportFeedback = false,
  showBallotData = false,
  showSignupNudge = true,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedElection, setSelectedElection] = useState(null);

  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackStatus, setFeedbackStatus] = useState("idle");
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const handleOpenCalendar = (election) => {
    setSelectedElection(transformDWElection(election));
    setModalOpen(true);
  };

  const handleSubmitFeedback = useCallback(async () => {
    if (!feedbackText.trim() || feedbackStatus === "submitting") return;
    setFeedbackStatus("submitting");
    try {
      await reportIssue({
        issueType: "missing_election",
        url: window.location.href,
        source: "DemocracyWorksElections",
        description: feedbackText.trim(),
      });
      setFeedbackStatus("idle");
      setFeedbackText("");
      setFeedbackOpen(false);
      setToast({ open: true, message: "Thanks! We'll look into it.", severity: "success" });
    } catch {
      setFeedbackStatus("idle");
      setToast({ open: true, message: "Something went wrong. Please try again.", severity: "error" });
    }
  }, [feedbackText, feedbackStatus]);

  const upcoming = elections.filter((e) => e.guidancePhase !== "past");

  if (upcoming.length === 0) return null;

  return (
    <Box sx={{ mt: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          mb: 1,
        }}
      >
        <Typography
          sx={{ fontSize: "0.7rem", color: "#0f172a", fontWeight: 500 }}
        >
          Powered by Democracy Works
        </Typography>
      </Box>

      <Stack spacing={2}>
        {upcoming.map((election, index) => {
          const daysUntil = getDaysUntil(election.date);
          const typeLabel =
            TYPE_LABELS[election.type] || election.type || "Election";

          const regOnline = election.registration?.online?.deadline?.date || null;
          const regByMail = election.registration?.byMail?.deadline?.date || null;
          const regInPerson = election.registration?.inPerson?.deadline?.date || null;
          const regDeadline = regOnline || regByMail || regInPerson || null;

          const mailBallotDeadline =
            election.voting?.byMail?.deadline?.ballotRequest?.date || null;
          const mailReturnDeadline =
            election.voting?.byMail?.deadline?.returnByMail?.date || null;

          const earlyStart = election.voting?.early?.startDate || null;
          const earlyEnd = election.voting?.early?.endDate || null;

          return (
            <Box
              key={`dw-${election.ocdId || index}-${election.date}`}
              sx={{
                backgroundColor: "#f8fafc",
                borderRadius: 3,
                p: 2.5,
              }}
            >
              {/* Header row */}
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    backgroundColor: `${theme.primary}15`,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    mr: 1.5,
                  }}
                >
                  <HowToVoteIcon
                    sx={{ fontSize: 20, color: theme.primary }}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 600,
                      color: "#0f172a",
                      lineHeight: 1.2,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {election.description || "Election"}
                  </Typography>
                </Box>
                <Chip
                  label={typeLabel}
                  size="small"
                  sx={{
                    ml: 1,
                    backgroundColor: `${theme.primary}15`,
                    color: theme.primary,
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    height: 24,
                  }}
                />
              </Box>

              {/* Date row */}
              <Box
                sx={{ display: "flex", alignItems: "center", mb: 1.5, gap: 1 }}
              >
                <CalendarTodayIcon sx={{ fontSize: 16, color: "#0f172a" }} />
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    color: "#0f172a",
                    fontWeight: 500,
                  }}
                >
                  {formatDate(election.date)}
                </Typography>
                {daysUntil !== null && (
                  <Box
                    sx={{
                      backgroundColor: theme.primary,
                      px: 1.5,
                      py: 0.25,
                      borderRadius: 3,
                      ml: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        color: "#fff",
                      }}
                    >
                      {daysUntil === 0
                        ? "Today!"
                        : daysUntil < 0
                        ? "Passed"
                        : `${daysUntil} days`}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Deadlines */}
              {(regDeadline || mailBallotDeadline || earlyStart) && (
                <Box sx={{ mb: 2 }}>
                  <DeadlineRow label="Register by" dateStr={regDeadline} />
                  <DeadlineRow label="Request mail ballot" dateStr={mailBallotDeadline} />
                  <DeadlineRow label="Return mail ballot" dateStr={mailReturnDeadline} />
                  {earlyStart && earlyEnd ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                      <EventNoteIcon sx={{ fontSize: 14, color: "#0f172a" }} />
                      <Typography sx={{ fontSize: "0.8rem", color: "#0f172a" }}>
                        <strong style={{ fontWeight: 600 }}>Early voting:</strong>{" "}
                        {formatDate(earlyStart)} – {formatDate(earlyEnd)}
                      </Typography>
                    </Box>
                  ) : earlyStart ? (
                    <DeadlineRow label="Early voting starts" dateStr={earlyStart} />
                  ) : null}
                </Box>
              )}

              {/* Add to Calendar button */}
              <Button
                fullWidth
                variant="contained"
                onClick={() => handleOpenCalendar(election)}
                sx={{
                  backgroundColor: theme.primary,
                  color: "#fff",
                  py: 1.25,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  display: "flex",
                  gap: 1,
                  "&:hover": {
                    backgroundColor: theme.primaryHover || "#065f46",
                    color: "#fff",
                  },
                  "& svg": { color: "#fff" },
                }}
              >
                <TbCalendarPlus style={{ fontSize: "1.1rem" }} />
                Add to Calendar
              </Button>

              {showBallotData && (
                <>
                  <BallotAccordion
                    election={election}
                    defaultExpanded={index === 0}
                  />
                  {showSignupNudge ? <SignupNudgeCard /> : null}
                </>
              )}
            </Box>
          );
        })}
      </Stack>

      {!hideReportFeedback && (
        <Typography
          sx={{
            mt: 2,
            textAlign: "center",
            fontSize: "0.8rem",
            color: "#0f172a",
            lineHeight: 1.5,
          }}
        >
          Don't see an election in your area?{" "}
          <span
            role="button"
            tabIndex={0}
            onClick={() => setFeedbackOpen(true)}
            onKeyDown={(e) => e.key === "Enter" && setFeedbackOpen(true)}
            style={{
              color: "var(--primary-color)",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Let us know
          </span>
        </Typography>
      )}

      {/* Missing election feedback modal */}
      <Dialog
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: "1.1rem",
            color: "#0f172a",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 0,
          }}
        >
          Report a missing election
          <IconButton
            onClick={() => setFeedbackOpen(false)}
            size="small"
            sx={{ color: "#64748b" }}
            aria-label="Close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <Typography
            variant="body2"
            sx={{ color: "#0f172a", mb: 0.5, fontSize: "0.85rem" }}
          >
            Tell us about the election we're missing and we'll work on adding it.
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#0f172a", mb: 2, fontSize: "0.78rem" }}
          >
            The more detail the better: include the date, location, and a link to an official source if you have one.
          </Typography>
          <TextField
            autoFocus
            multiline
            minRows={3}
            maxRows={6}
            fullWidth
            placeholder="e.g. City council election in Springfield, IL on June 3, 2026 — https://springfield.gov/elections"
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: "0.9rem",
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            onClick={handleSubmitFeedback}
            disabled={!feedbackText.trim() || feedbackStatus === "submitting"}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              backgroundColor: theme.primary,
              color: "#fff",
              px: 3,
              boxShadow: "none",
              "&:hover": { backgroundColor: theme.primaryHover, boxShadow: "none" },
              "&.Mui-disabled": { backgroundColor: "#e2e8f0", color: "#94a3b8" },
            }}
          >
            {feedbackStatus === "submitting" ? (
              <CircularProgress size={18} sx={{ color: "#fff", mr: 1 }} />
            ) : null}
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <CalendarModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedElection(null);
        }}
        election={selectedElection}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          variant="filled"
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DemocracyWorksElections;
